/* eslint-disable dot-notation */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

import DomainManager from '../utils/DomainManager';
import { ResponseType } from '../api/types';
import { MrtAxiosException } from '../api/mrtAxiosException';
import getEncodedQueryString from '../utils/getEncodedQueryString';
import getSigninDeepLink from '../utils/getSigninDeepLink';
import CookieManager, { CookieKey } from '../utils/cookieManager';

// mrtAxios 인스턴스 초기화 여부 판별 플래그
let initialized = false;

// status >= 400 이지만 Error가 아닌, Success처리를 위한 예외 에러코드
const errorExceptionArr = ['authentication.error.signupFailed'];

export interface MrtAxiosOptions {
  isWebView: boolean;
}
const COMMON_CLIENT_ERROR_CODE = 'common.error.clientError';
const COMMON_SERVER_ERROR_CODE = 'common.error.serverError';

const mrtAxios = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

function setAccessToken() {
  const token = CookieManager.get(CookieKey.MRT_ACCESS_TOKEN);

  if (token) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    mrtAxios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
}

function getOnBeforeRequest() {
  return function (config: InternalAxiosRequestConfig) {
    return config;
  };
}

function getOnRequestRejected() {
  return function (error: AxiosError) {
    const { message, stack = '' } = error;

    const mrtAxiosException = new MrtAxiosException({
      isMrtError: false,
      status: 500,
      code: COMMON_CLIENT_ERROR_CODE,
      message,
      error,
      stack,
    });

    return Promise.reject(mrtAxiosException);
  };
}

function getOnResponseFulfilled(targetEnv: string, option?: Record<string, any>) {
  return function (response: AxiosResponse<ResponseType>) {
    const { status, message, code } = response?.data?.result || {};
    const isExceptionCode = errorExceptionArr.includes(code);
    const isError = !isExceptionCode && status >= 400;
    const isWebView = Boolean(option?.isWebView);
    const domainManager = new DomainManager(targetEnv);

    // MRT 응답 코드 >= 400 이면 에러 처리
    if (isError) {
      // 권한 문제라면 로그인 페이지로 리다이렉트
      if (status === 401) {
        if (typeof window === 'undefined') {
          return Promise.reject(
            new MrtAxiosException({
              isMrtError: true,
              status,
              code,
              message,
              error: new Error(message),
            }),
          );
        }

        if (isWebView) {
          const deepLink = getSigninDeepLink();
          window.location.href = deepLink;
        } else {
          // query는 한 번만 encoding 된 상태로 보내야 한다.
          const queryString = getEncodedQueryString({
            stringifyOptions: { addQueryPrefix: true },
          });
          const fullPath = `${domainManager.getTravelerServiceDomain()}${window.location.pathname}`;
          const encodedReturnUrl = `${encodeURIComponent(fullPath)}${queryString}`;

          window.location.href = `${domainManager.getAuthServiceDomain()}/users/sign_in?return_to=${encodedReturnUrl}`;
        }
      }

      const mrtAxiosException = new MrtAxiosException({
        isMrtError: true,
        status,
        code,
        message,
        error: new Error(message),
      });

      return Promise.reject(mrtAxiosException);
    }

    return response;
  };
}

function getOnResponseRejected() {
  return function (error: AxiosError<ResponseType>) {
    const {
      status = 500,
      code = COMMON_SERVER_ERROR_CODE,
      message = '',
    } = error?.response?.data?.result || {};

    const mrtAxiosException = new MrtAxiosException({
      isMrtError: false,
      status,
      code,
      message,
      stack: error?.stack || '',
      error,
    });

    return Promise.reject(mrtAxiosException);
  };
}

function getAxios(targetEnv: string, option?: Record<string, any>) {
  // 최초 호출일 경우에만 초기화 로직 수행
  if (!initialized) {
    const isWebView = Boolean(option?.isWebView);

    if (!isWebView) {
      setAccessToken();
    }
    const onBeforeRequest = getOnBeforeRequest();
    const onRequestRejected = getOnRequestRejected();

    const onResponseFulfilled = getOnResponseFulfilled(targetEnv, option);
    const onResponseRejected = getOnResponseRejected();

    mrtAxios.interceptors.request.use(onBeforeRequest, onRequestRejected);
    mrtAxios.interceptors.response.use(onResponseFulfilled, onResponseRejected);

    initialized = true;
  }

  return mrtAxios;
}

export default (targetEnv: string, option?: Record<string, any>) => {
  return getAxios(targetEnv, option);
};
